import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogOut, TrendingUp, Wrench, ExternalLink } from "lucide-react";

function today() {
  return new Date().toISOString().slice(0, 10);
}
function rupiah(n) {
  try {
    return "Rp " + Number(n || 0).toLocaleString("id-ID");
  } catch { return "Rp " + n; }
}
function fmtTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(11, 16); }
}
function paymentBadge(pm) {
  const p = (pm || "CASH").toUpperCase();
  const styles = {
    CASH:     "bg-emerald-100 text-emerald-700 border-emerald-300",
    TRANSFER: "bg-blue-100 text-blue-700 border-blue-300",
    QRIS:     "bg-purple-100 text-purple-700 border-purple-300",
  };
  return styles[p] || "bg-zinc-100 text-zinc-700 border-zinc-300";
}
// v84.169 — Payment method pill styles utk breakdown di header
const PM_PILL_COLORS = {
  TRANSFER: "bg-blue-600 text-white",
  CASH:     "bg-emerald-500 text-white",
  QRIS:     "bg-fuchsia-500 text-white",
};

export default function TechRekap() {
  const { tech, logout } = useAuth();
  const navigate = useNavigate();
  // v84.169 — Halaman selalu tampilkan REKAP HARI INI saja. Date picker dihapus.
  const [data, setData] = useState({ rows: [], total_today: 0, total_range: 0, tech: null });
  const [busy, setBusy] = useState(false);
  // v84.95 — Kasbon multi-entry state (Task A refactor)
  const [kasbonPersonnel, setKasbonPersonnel] = useState([]);
  const [kasbonEntries, setKasbonEntries] = useState([
    { personnel_id: "", nominal: "" },
    { personnel_id: "", nominal: "" },
    { personnel_id: "", nominal: "" },
  ]);
  const [kasbonNote, setKasbonNote] = useState("");
  const [kasbonBusy, setKasbonBusy] = useState(false);
  const [kasbonLoaded, setKasbonLoaded] = useState(false);

  async function load() {
    setBusy(true);
    try {
      // v84.169 — Query hanya hari ini (from=to=today).
      const d = today();
      const r = await api.techRekap(d, d);
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data");
    } finally { setBusy(false); }
  }

  async function loadKasbon() {
    try {
      const r = await api.listKasbon(today());
      setKasbonPersonnel(r.personnel_options || []);
      // Build 3 slots: fill from saved entries; kalau opsi lbh sedikit dari 3, tetap 3 row
      const saved = r.entries || [];
      const slots = [0, 1, 2].map(i => {
        const e = saved[i] || {};
        return {
          personnel_id: e.personnel_id || "",
          nominal: e.nominal ? String(e.nominal) : "",
        };
      });
      setKasbonEntries(slots);
      setKasbonNote(r.note || "");
      setKasbonLoaded(true);
    } catch (e) { /* silent */ }
  }

  function updateEntry(idx, key, val) {
    const copy = [...kasbonEntries];
    copy[idx] = { ...copy[idx], [key]: val };
    setKasbonEntries(copy);
  }

  async function saveKasbon() {
    setKasbonBusy(true);
    try {
      const entries = kasbonEntries
        .map(e => ({
          personnel_id: (e.personnel_id || "").trim() || null,
          nominal: parseFloat(String(e.nominal || "0").replace(/[^\d.]/g,"")) || 0,
        }))
        .filter(e => e.personnel_id || e.nominal > 0);
      await api.upsertKasbon({
        date: today(),
        entries,
        note: kasbonNote,
      });
      const total = entries.reduce((a, e) => a + e.nominal, 0);
      toast.success(`Kasbon tersimpan (${entries.length} entri, total Rp ${total.toLocaleString('id-ID')})`);
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally { setKasbonBusy(false); }
  }

  useEffect(() => { load(); loadKasbon(); /* eslint-disable-next-line */ }, []);

  async function handleLogout() {
    await logout();
    navigate("/tech-login", { replace: true });
  }

  const t = tech || data.tech;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="tech-rekap-page">
      {/* Top header */}
      <div className="bg-gradient-to-r from-[#0A0E1A] via-[#0F1729] to-[#0A0E1A] text-white sticky top-0 z-10 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-[#E65100] to-[#F57C00] shrink-0">
            <Wrench className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-['Manrope'] font-bold text-sm leading-none">GLOBAL AC · Rekap Kerja</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-300/80 mt-1 truncate">
              {t?.name || "-"} · {t?.username || "-"}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-white/70 hover:text-white hover:bg-white/10 gap-1.5"
            data-testid="tech-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Keluar</span>
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        {/* v84.169 — Top cards: Pendapatan (kiri) + Payment method breakdown (kanan) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-border p-4 shadow-sm" data-testid="total-today-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              Pendapatan Hari Ini
            </div>
            <div className="font-['Manrope'] font-bold text-2xl text-[#E65100] tracking-tight" data-testid="total-today-value">
              {rupiah(data.total_today)}
            </div>
          </div>
          {/* Payment method breakdown — hitung dari data.rows */}
          <div className="bg-white rounded-xl border border-border p-4 shadow-sm" data-testid="payment-breakdown-card">
            {(() => {
              const bd = { TRANSFER: 0, CASH: 0, QRIS: 0 };
              (data.rows || []).forEach(r => {
                const pm = (r.payment_method || "CASH").toUpperCase();
                if (pm in bd) bd[pm] += Number(r.grand_total || 0);
              });
              const orderKeys = ["TRANSFER", "CASH", "QRIS"];
              return (
                <div className="space-y-1.5">
                  {orderKeys.map(pm => (
                    <div key={pm}
                      className="flex items-center justify-between gap-3"
                      data-testid={`pm-breakdown-${pm.toLowerCase()}`}>
                      <span className={`inline-block px-4 py-1 rounded-md font-['Manrope'] font-bold text-[13px] tracking-wider ${PM_PILL_COLORS[pm]}`}>
                        {pm === "TRANSFER" ? "Transfer" : pm === "CASH" ? "Cash" : "Qris"}
                      </span>
                      <span className="font-mono font-bold text-sm text-zinc-800"
                        data-testid={`pm-amount-${pm.toLowerCase()}`}>
                        {rupiah(bd[pm])}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* v84.95 — Kasbon multi-entry panel (Task A refactor) */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-300 p-4 shadow-sm"
             data-testid="kasbon-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-800 font-bold">
              <TrendingUp className="w-3.5 h-3.5" />
              Kasbon Hari Ini · Armada {t?.name || "-"}
            </div>
            {kasbonPersonnel.length === 0 && kasbonLoaded && (
              <div className="text-[10px] text-red-700 font-semibold">
                Admin belum assign personel ke armada Anda hari ini.
              </div>
            )}
          </div>

          {/* Header row */}
          <div className="grid grid-cols-[1fr_140px] gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold">Teknisi</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold text-right">Kasbon (Rp)</div>
          </div>

          {/* 3 slots */}
          <div className="space-y-1.5">
            {kasbonEntries.map((e, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px] gap-2"
                   data-testid={`kasbon-entry-${i}`}>
                <select
                  value={e.personnel_id}
                  onChange={(ev) => updateEntry(i, "personnel_id", ev.target.value)}
                  disabled={kasbonPersonnel.length === 0}
                  className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-[13px] font-medium"
                  data-testid={`kasbon-personnel-${i}`}
                >
                  <option value="">— pilih teknisi —</option>
                  {kasbonPersonnel.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                  {/* Kalau saved personnel_id tidak ada di options, tetap tampilkan */}
                  {e.personnel_id && !kasbonPersonnel.find(p => p.id === e.personnel_id) && (
                    <option value={e.personnel_id}>(tidak di-assign hari ini)</option>
                  )}
                </select>
                <Input
                  type="number"
                  min="0"
                  step="1000"
                  value={e.nominal}
                  onChange={(ev) => updateEntry(i, "nominal", ev.target.value)}
                  placeholder="0"
                  className="rounded-sm h-9 font-mono font-bold text-right bg-orange-600 border-orange-700 text-white placeholder:text-orange-100 focus-visible:ring-orange-400"
                  data-testid={`kasbon-nominal-${i}`}
                />
              </div>
            ))}
          </div>

          {/* Note + Save */}
          <div className="flex items-end gap-2 mt-3">
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-1">
                Catatan (opsional)
              </label>
              <Input
                type="text"
                value={kasbonNote}
                onChange={(e) => setKasbonNote(e.target.value)}
                placeholder="mis. beli spare part"
                className="rounded-sm h-9"
                data-testid="kasbon-note-input"
              />
            </div>
            <Button
              onClick={saveKasbon}
              disabled={kasbonBusy || !kasbonLoaded}
              className="rounded-sm h-9 bg-orange-600 hover:bg-orange-700 text-white font-bold"
              data-testid="kasbon-save-btn">
              {kasbonBusy ? "Menyimpan…" : "Simpan"}
            </Button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1.5">
            Nilai per teknisi akan otomatis dipotong dari gaji masing-masing di siklus payroll berikutnya. Bisa 2-3 orang.
          </div>
        </div>

        {/* v84.169 — Date picker dihapus. Halaman default = HARI INI. */}

        {/* Table */}
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border bg-zinc-50 flex items-center gap-2">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">Riwayat Pengerjaan</div>
            <div className="ml-auto text-[11px] font-mono text-zinc-500" data-testid="rows-count">
              {data.rows.length} entri
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="rekap-table">
              <thead className="bg-zinc-50 border-b border-border">
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  <th className="px-3 py-2">Tanggal</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Layanan</th>
                  <th className="px-3 py-2 text-right">Total Tagihan</th>
                  <th className="px-3 py-2 text-center">Metode</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-zinc-500 text-[12px]" data-testid="empty-row">
                      Belum ada pengerjaan tercatat untuk hari ini.
                    </td>
                  </tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.wo_id} className="border-b border-border hover:bg-zinc-50/60" data-testid={`rekap-row-${r.wo_id}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-mono text-[11px] font-semibold">{r.date}</div>
                      <div className="text-[10px] text-zinc-500">{fmtTime(r.scheduled_start)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-zinc-800">{r.customer_name}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">{r.address}</div>
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <div className="text-zinc-700 truncate">{r.service}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-zinc-900">
                      {rupiah(r.grand_total)}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${paymentBadge(r.payment_method)}`}>
                        {r.payment_method}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {r.google_event_id && (
                        <a
                          href={`/view-invoice/${r.google_event_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-semibold"
                          data-testid={`invoice-link-${r.wo_id}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Invoice
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-center text-[10px] text-zinc-400 py-4">
          Rekap otomatis difilter untuk armada Anda hari ini saja.
        </div>
      </div>
    </div>
  );
}
